import {
  AccordionElement,
  Alert,
  AlertVariants,
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  ButtonWrapper,
  CopyButton,
  FormControlSizes,
  FormGroup,
  Grid,
  Icon,
  IconName,
  Image,
  Input,
  isTalosUser,
  MixpanelEvent,
  PopperHeader,
  Stack,
  Tab,
  TabList,
  TabPanels,
  Tabs,
  TabSize,
  useMixpanel,
  useTabs,
  type TabProps,
} from '@talos/kyoko';
import type { DragEvent } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useAddablePanelTypes } from '../../components/AppLayout/hooks/useAddablePanelTypes';
import { useEditLayoutControls } from '../../components/AppLayout/hooks/useEditLayoutControls';
import { TabIconButton } from '../../components/Layout/styles';
import { useFeatureFlag, useUser } from '../../hooks';
import { useAppLayoutContext } from '../../providers/AppLayoutContextProvider/AppLayoutContextProvider';
import { LAYOUT_HEADER_HEIGHT } from '../../styles/dimensions';
import type { LayoutSection, PanelComponentSpec } from '../../types/LayoutConfig';
import { LAYOUT_SECTION_DISPLAY_LABEL, VALID_SECTIONS } from '../../types/LayoutConfig';

const MENU_MIN_WIDTH = 370;

interface LayoutMenuCategory {
  key: string;
  label: string;
  panels: PanelComponentSpec[];
}

function categoryTestId(key: string) {
  return `layout-menu-category-${key.replace(/[^a-zA-Z0-9]+/g, '-')}`;
}

/** Ungrouped panels → one bucket per `panel.section` (Trading / Monitoring); grouped → one per `group.type`, ordered section buckets then by group label. */
export function buildLayoutMenuCategories(panels: PanelComponentSpec[]): LayoutMenuCategory[] {
  const map = new Map<string, LayoutMenuCategory>();
  for (const panel of panels) {
    const key = panel.group ? `group:${panel.group.type}` : `section:${panel.section}`;
    const label = panel.group ? panel.group.label : LAYOUT_SECTION_DISPLAY_LABEL[panel.section];
    const existing = map.get(key);
    if (existing) {
      existing.panels.push(panel);
    } else {
      map.set(key, { key, label, panels: [panel] });
    }
  }

  const sectionCats: LayoutMenuCategory[] = [];
  const groupCats: LayoutMenuCategory[] = [];
  for (const cat of map.values()) {
    if (cat.key.startsWith('section:')) {
      sectionCats.push(cat);
    } else {
      groupCats.push(cat);
    }
  }

  const orderedSections = sectionCats.toSorted((a, b) => {
    const sa = a.key.slice('section:'.length) as LayoutSection;
    const sb = b.key.slice('section:'.length) as LayoutSection;
    return VALID_SECTIONS.indexOf(sa) - VALID_SECTIONS.indexOf(sb);
  });
  const orderedGroups = groupCats.toSorted((a, b) => a.label.localeCompare(b.label));

  return [...orderedSections, ...orderedGroups];
}

function panelMatchesFilter(panel: PanelComponentSpec, filter: string) {
  const q = filter.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return panel.label.toLowerCase().includes(q);
}

export function LayoutMenu({
  close,
  layoutSection,
  additionalContentSections,
}: {
  close: () => void;
  layoutSection: LayoutSection;
  additionalContentSections: LayoutSection[];
}) {
  const user = useUser();
  const [filter, setFilter] = useState('');
  const [showGuide, setShowGuide] = useState(true);
  const { type } = useTheme();
  const mixpanel = useMixpanel();
  const { dockviewApiRef, resetLayout } = useAppLayoutContext();

  const {
    undoStack,
    redoStack,
    layoutHasChanged,
    layoutOptions,
    selectedLayout,
    handleResetToInitial,
    handleChangeLayout,
    handleUndo,
    handleRedo,
  } = useEditLayoutControls();

  const { enableTradingSpaces } = useFeatureFlag();

  const addablePanelTypes = useAddablePanelTypes(layoutSection, additionalContentSections);

  const categories = useMemo(() => buildLayoutMenuCategories(addablePanelTypes), [addablePanelTypes]);

  const categoryTabItems = useMemo<TabProps[]>(
    () => categories.map(cat => ({ id: cat.key, label: cat.label })),
    [categories]
  );
  const categorySignature = useMemo(() => categoryTabItems.map(t => t.id).join('|'), [categoryTabItems]);

  /** Category labels + selection; `useTabs` only seeds from `initialItems` on mount, so we sync in `useLayoutEffect` when categories change. */
  const tabs = useTabs({
    initialItems: categoryTabItems,
    allowClosingLastTab: false,
  });

  const {
    setItems,
    setSelectedIndex,
    items: layoutMenuTabItems,
    updateItem: _updateItem,
    reorderItems: _reorderItems,
    clone: _clone,
    isAdding: _isAdding,
    ...tabsPropsForTabs
  } = tabs;

  useLayoutEffect(() => {
    setItems(categoryTabItems);
    setSelectedIndex(i => Math.min(i, Math.max(0, categoryTabItems.length - 1)));
  }, [categorySignature, categoryTabItems, setItems, setSelectedIndex]);

  /** Restores the category tab when the user clears the search field. */
  const categoryIndexBeforeSearchRef = useRef(0);

  const clampedCategoryIndex = categories.length === 0 ? 0 : Math.min(tabs.selectedIndex, categories.length - 1);

  const searchActive = filter.trim().length > 0;

  const searchMatches = useMemo(() => {
    if (!searchActive) {
      return [];
    }
    const out: { panel: PanelComponentSpec; categoryLabel: string }[] = [];
    for (const cat of categories) {
      for (const panel of cat.panels) {
        if (panelMatchesFilter(panel, filter)) {
          out.push({ panel, categoryLabel: cat.label });
        }
      }
    }
    return out;
  }, [categories, filter, searchActive]);

  const handleFilterChange = useCallback(
    (next: string) => {
      const wasEmpty = filter.trim() === '';
      const nextEmpty = next.trim() === '';
      if (wasEmpty && !nextEmpty) {
        categoryIndexBeforeSearchRef.current = clampedCategoryIndex;
      }
      if (!wasEmpty && nextEmpty && categories.length > 0) {
        setSelectedIndex(Math.min(categoryIndexBeforeSearchRef.current, Math.max(0, categories.length - 1)));
      }
      setFilter(next);
    },
    [categories.length, clampedCategoryIndex, filter, setSelectedIndex]
  );

  /** Pass panel spec via dataTransfer for dockview drop; close popper so it does not cover the layout. */
  const handleDragStart = useCallback(
    (e: DragEvent<HTMLButtonElement>, panelSpec: PanelComponentSpec) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.dropEffect = 'move';
      e.dataTransfer.setData('text/json', JSON.stringify(panelSpec));
      close();
    },
    [close]
  );

  /** Reset the layout to the initial layout for this session */
  const onResetToInitial = useCallback(() => {
    handleResetToInitial();
    close();
  }, [close, handleResetToInitial]);

  /** Reset the layout to the layout of its template */
  const onResetLayout = useCallback(() => {
    resetLayout();
    mixpanel.track(MixpanelEvent.ResetLayout);
    close();
  }, [close, resetLayout, mixpanel]);

  const renderPanelTile = useCallback(
    (panelType: PanelComponentSpec, categoryHint?: string) => (
      <ContentItem
        data-label={panelType.label}
        data-testid={`add-panel-${panelType.type}`}
        key={`${panelType.type}-${categoryHint ?? 'default'}`}
        draggable={true}
        onDragStart={e => handleDragStart(e, panelType)}
      >
        <Box>
          <Image
            src={`/assets/components/${type}/${panelType.type}.svg`}
            fallbackSrc={`/assets/components/${type}/blotter.svg`}
            alt={panelType.label}
            draggable={false}
          />
          <Box mt="spacing4">{panelType.label}</Box>
          {categoryHint != null && (
            <CategoryHint color="colorTextMuted" mt="spacing4">
              {categoryHint}
            </CategoryHint>
          )}
        </Box>
      </ContentItem>
    ),
    [handleDragStart, type]
  );

  return (
    <>
      <Stack
        gap="spacing8"
        minWidth={MENU_MIN_WIDTH}
        data-testid="layout-menu"
        maxHeight={`calc(100vh - ${LAYOUT_HEADER_HEIGHT * 2}px)`}
        overflow="hidden"
      >
        <PopperHeader
          title="Modules"
          suffix={
            <TabIconButton onClick={() => setShowGuide(prev => !prev)} icon={IconName.LightBulb} ghost={!showGuide} />
          }
        />
        <Grid
          gridTemplateColumns="min-content min-content 1fr 1fr"
          gridAutoFlow="column"
          px="spacing8"
          pt="spacing8"
          gap="spacing8"
          borderTop="1px solid"
          borderColor="backgroundDivider"
        >
          <Button
            size={FormControlSizes.Small}
            startIcon={IconName.ArrowUndo}
            onClick={handleUndo}
            disabled={undoStack.length === 0}
          >
            Undo
          </Button>
          <Button
            size={FormControlSizes.Small}
            endIcon={IconName.ArrowRedo}
            onClick={handleRedo}
            disabled={redoStack.length === 0}
          >
            Redo
          </Button>
          <Button
            onClick={onResetToInitial}
            disabled={!layoutHasChanged}
            variant={ButtonVariants.Default}
            width="100%"
            size={FormControlSizes.Small}
          >
            Revert changes
          </Button>
          <Button onClick={onResetLayout} variant={ButtonVariants.Default} width="100%" size={FormControlSizes.Small}>
            Reset layout
          </Button>
        </Grid>
        <Box px="spacing8">
          <Input
            data-testid="layout-menu-search"
            suffix={<Icon icon={IconName.Search} />}
            value={filter}
            onChange={e => handleFilterChange(e.target.value)}
          />
        </Box>
        <AccordionElement isOpen={showGuide} px="spacing8">
          <Alert onDismiss={() => setShowGuide(false)} variant={AlertVariants.Guide}>
            Drag modules to customize your space.
          </Alert>
        </AccordionElement>
        <Stack flex="1 1 auto" minHeight={0} overflow="hidden">
          {/* Non-empty search: global matches in one grid (tabs hidden); empty search: category tabs + one grid per tab */}
          {searchActive ? (
            <Box overflowY="scroll" flex="1 1 auto" minHeight={200} scrollbarGutter="stable">
              {searchMatches.length === 0 ? (
                <Box pb="spacing8" color="colorTextMuted">
                  No modules match your search.
                </Box>
              ) : (
                <Grid gap="spacing4" pb="spacing8" gridTemplateColumns="1fr 1fr">
                  {searchMatches.map(({ panel, categoryLabel }) => renderPanelTile(panel, categoryLabel))}
                </Grid>
              )}
            </Box>
          ) : categories.length === 0 ? (
            <Box pb="spacing8" color="colorTextMuted">
              No modules available.
            </Box>
          ) : (
            <Tabs
              {...tabsPropsForTabs}
              selectedIndex={clampedCategoryIndex}
              display="flex"
              flexDirection="column"
              flex="1 1 auto"
              minHeight={0}
              size={TabSize.Small}
            >
              <TabList suppressOverflowList px={0} mb="spacing8" flex="0 0 auto" isBordered={true}>
                {layoutMenuTabItems.map(tab => (
                  <Tab key={tab.id} id={tab.id} label={tab.label} data-testid={categoryTestId(tab.id!)} />
                ))}
              </TabList>
              <Box overflowY="scroll" flex="1 1 auto" minHeight={200} scrollbarGutter="stable both-edges">
                <TabPanels position="relative">
                  {categories.map(cat => (
                    <Grid key={cat.key} gap="spacing4" pb="spacing8" gridTemplateColumns="1fr 1fr">
                      {cat.panels.map(panel => renderPanelTile(panel))}
                    </Grid>
                  ))}
                </TabPanels>
              </Box>
            </Tabs>
          )}
        </Stack>
        {!enableTradingSpaces && (
          <FormGroup label="Layouts" px="spacing8" mb={0}>
            <ButtonGroup orientation="horizontal" size={FormControlSizes.Small}>
              {Object.values(layoutOptions ?? {}).map(layout => (
                <Button
                  width="50%"
                  onClick={() => handleChangeLayout(layout.id)}
                  variant={layout.id === selectedLayout?.id ? ButtonVariants.Priority : ButtonVariants.Default}
                  key={layout.id}
                >
                  {layout.label}
                </Button>
              ))}
            </ButtonGroup>
          </FormGroup>
        )}
        {(isTalosUser(user) || user.IsMorphed) && (
          <Box px="spacing8" pb="spacing8" gap="spacing8">
            <CopyButton width="100%" value={JSON.stringify(dockviewApiRef?.current?.toJSON(), null, 2)}>
              Copy JSON
            </CopyButton>
          </Box>
        )}
      </Stack>
    </>
  );
}

const CategoryHint = styled(Box)`
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ContentItem = styled(ButtonWrapper)`
  overflow: hidden;
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colorTextDefault};
  background: ${({ theme }) => theme.colors.gray['020']};

  &:hover {
    color: ${({ theme }) => theme.colorTextImportant};
  }
`;
ContentItem.defaultProps = {
  size: FormControlSizes.Tiny,
  height: 76,
  ghost: true,
  style: { paddingTop: '6px', paddingBottom: '6px', borderRadius: '3px' },
};
